import { Link } from '@tanstack/react-router'

import { OutcomeBar } from '@/components/benchmark/OutcomeBar'
import { StatRow, StatTile } from '@/components/metrics/StatTile'
import { PageHeader, Panel } from '@/components/ui/Panel'
import { EmptyState, QueryBoundary, Skeleton } from '@/components/ui/States'
import { useBenchmarkRuns } from '@/features/benchmark/useBenchmark'
import { formatMs, formatPercent, formatRelative } from '@/lib/utils/format'

export function BenchmarkPage() {
  const { data, isLoading, error, refetch } = useBenchmarkRuns(20)
  const runs = data?.runs ?? []
  const latest = runs[0]

  return (
    <div>
      <PageHeader
        title="Benchmark"
        description="Fifty natural-language queries, run against the real MCP server, measuring whether the model picks the right tool."
      />

      <QueryBoundary
        isLoading={isLoading}
        error={error}
        onRetry={() => void refetch()}
        isEmpty={runs.length === 0}
        loadingFallback={<Skeleton className="h-28 w-full" />}
        emptyState={
          <Panel>
            <EmptyState
              title="No benchmark runs stored yet."
              hint={
                <>
                  Run the suite from the repository root:{' '}
                  <code className="mono rounded-sm bg-raised px-1.5 py-0.5 text-ink-dim">
                    mcp-benchmark run --mode llm
                  </code>
                  . The deterministic baseline needs no API key:{' '}
                  <code className="mono rounded-sm bg-raised px-1.5 py-0.5 text-ink-dim">
                    mcp-benchmark run --mode heuristic
                  </code>
                  .
                </>
              }
            />
          </Panel>
        }
      >
        {latest ? (
          <>
            <StatRow>
              <StatTile
                label="Tool selection accuracy"
                value={formatPercent(latest.tool_selection_accuracy, 0)}
                tone={latest.tool_selection_accuracy >= 0.85 ? 'ok' : 'miss'}
                hint="Queries where the expected tool was chosen, including ones whose execution then failed — the choice was still right."
                footer={`${latest.correct_tool + latest.tool_failed} of ${latest.total_queries} queries`}
              />
              <StatTile
                label="Wrong tool"
                value={latest.wrong_tool}
                tone={latest.wrong_tool > 0 ? 'miss' : 'default'}
                hint="A different tool was chosen. Fixed by rewriting tool descriptions."
                footer="fix the descriptions"
              />
              <StatTile
                label="Tool failed"
                value={latest.tool_failed}
                tone={latest.tool_failed > 0 ? 'fail' : 'default'}
                hint="The right tool was chosen but execution errored. Fixed in code or credentials."
                footer="fix the code"
              />
              <StatTile
                label="p95 latency"
                value={formatMs(latest.p95_latency_ms).replace(' ms', '').replace(' s', '')}
                unit={latest.p95_latency_ms >= 1000 ? 's' : 'ms'}
                hint="Model selection time plus execution time."
                footer={`median ${formatMs(latest.p50_latency_ms)}`}
              />
            </StatRow>

            <Panel
              title="Latest run"
              description={`${latest.mode}${latest.model ? ` · ${latest.model}` : ''} — ${formatRelative(latest.started_at)}`}
              actions={
                <Link
                  to="/benchmark/$runUid"
                  params={{ runUid: latest.run_uid }}
                  className="text-xs text-primary underline-offset-2 hover:underline"
                >
                  Open all {latest.total_queries} queries
                </Link>
              }
              className="mt-4"
            >
              <OutcomeBar
                correct={latest.correct_tool}
                wrongTool={latest.wrong_tool}
                toolFailed={latest.tool_failed}
                noTool={latest.no_tool_selected}
                total={latest.total_queries}
              />
            </Panel>

            <Panel title="Run history" className="mt-4">
              <div className="overflow-x-auto">
                <table className="data-table">
                  <thead>
                    <tr>
                      <th scope="col" className="w-40">
                        Started
                      </th>
                      <th scope="col" className="w-28">
                        Mode
                      </th>
                      <th scope="col">Model</th>
                      <th scope="col" className="w-24 text-right">
                        Queries
                      </th>
                      <th scope="col" className="w-28 text-right">
                        Accuracy
                      </th>
                      <th scope="col" className="w-24 text-right">
                        Wrong
                      </th>
                      <th scope="col" className="w-24 text-right">
                        Failed
                      </th>
                      <th scope="col" className="w-28 text-right">
                        p95
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {runs.map((run) => (
                      <tr key={run.run_uid} data-clickable="true">
                        <td>
                          <Link
                            to="/benchmark/$runUid"
                            params={{ runUid: run.run_uid }}
                            className="text-ink hover:text-primary-bright"
                          >
                            {formatRelative(run.started_at)}
                          </Link>
                        </td>
                        <td className="mono text-ink-dim">{run.mode}</td>
                        <td className="mono truncate text-ink-muted">{run.model ?? '—'}</td>
                        <td className="mono text-right text-ink-dim">{run.total_queries}</td>
                        <td className="mono text-right text-ink">
                          {formatPercent(run.tool_selection_accuracy, 0)}
                        </td>
                        <td className="mono text-right">
                          <span className={run.wrong_tool > 0 ? 'text-miss' : 'text-ink-faint'}>
                            {run.wrong_tool}
                          </span>
                        </td>
                        <td className="mono text-right">
                          <span className={run.tool_failed > 0 ? 'text-fail' : 'text-ink-faint'}>
                            {run.tool_failed}
                          </span>
                        </td>
                        <td className="mono text-right text-ink-muted">
                          {formatMs(run.p95_latency_ms)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </Panel>
          </>
        ) : null}
      </QueryBoundary>
    </div>
  )
}
